import { useState } from "react";
import Image from "next/image";
import { Modal } from "@/components/shared/data-display/modal/modal";
import styles from "./work-large-cover.module.scss";

export interface WorkLargeCoverProps {
  title: string;
  coverUrl: string;
  largeCoverUrl: string | null;
  coverClassName?: string;
}

/**
 * Displays a responsive cover and, when a large image exists, opens it in a modal.
 * @param root0 Component props.
 * @param root0.title Selected edition title.
 * @param root0.coverUrl Selected edition cover image.
 * @param root0.largeCoverUrl Best available large cover image.
 * @param root0.coverClassName Optional class for the responsive cover image.
 * @returns An interactive cover when enlargement is available, otherwise a plain image.
 */
const WorkLargeCover = ({
  title,
  coverUrl,
  largeCoverUrl,
  coverClassName,
}: WorkLargeCoverProps) => {
  const [open, setOpen] = useState(false);
  const cover = (
    <Image
      src={coverUrl}
      alt={`Cover of ${title}`}
      width={300}
      height={450}
      priority
      sizes="(max-width: 599px) 110px, (max-width: 767px) 130px, (max-width: 899px) 150px, (max-width: 1199px) 210px, 270px"
      className={coverClassName}
    />
  );

  if (!largeCoverUrl) return cover;

  return (
    <>
      <button
        type="button"
        className={styles.coverButton}
        onClick={() => setOpen(true)}
        aria-label={`View larger cover of ${title}`}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {cover}
      </button>
      <Modal
        open={open}
        onOpenChange={setOpen}
        isDismissible
        showDismissIcon={false}
        isFixedWidth
        primaryButtonText=""
        containerClassName={styles.modal}
      >
        <div className={styles.modalContent}>
          <h2 className="tb-hiddenText">Large cover of {title}</h2>
          {/* eslint-disable-next-line @next/next/no-img-element -- The large-cover modal must preserve the source asset's natural dimensions and avoid optimizer upscaling. */}
          <img
            src={largeCoverUrl}
            alt={`Large cover of ${title}`}
            className={styles.largeCover}
          />
        </div>
      </Modal>
    </>
  );
};

export default WorkLargeCover;
